<template>
  <div class="smethod">
    <div class="smethod__row" v-for="(s, i) in steps" :key="i">
      <div class="smethod__num">{{ String(i + 1).padStart(2, '0') }}</div>
      <div class="smethod__body">
        <h4 class="smethod__h">{{ s.title }}</h4>
        <p class="smethod__p" v-html="s.body" />
      </div>
      <figure v-if="s.img" class="smethod__fig" :class="{ 'smethod__fig--diagram': s.diagram }">
        <img :src="s.img" :alt="s.alt || ''" />
        <figcaption>{{ s.cap }}</figcaption>
      </figure>
    </div>
  </div>
</template>

<script setup lang="ts">
const steps = [
  {
    title: 'Clay maquette',
    body: 'The form is found by shaping clay. The geometry then gets formalised into a mathematical surface — typically a <em>ruled surface</em>, a curve generated by the motion of a straight line.',
    img: 'https://upload.wikimedia.org/wikipedia/commons/c/c2/2017-05-28_via_Fantiscritti_con_Chiesa_Spirito_Santo_Miseglia_01.jpg',
    alt: 'Via Fantiscritti',
    cap: 'Via Fantiscritti 11 — the workshop sits on this street.',
  },
  {
    title: 'Ruled-surface generation',
    body: 'The stone rotates on its axis; a straight diamond wire descends. The trace is a <strong>hyperbolic paraboloid</strong> — a curved surface that contains no centre, generated by the motion of a single straight line.',
    img: 'https://upload.wikimedia.org/wikipedia/commons/8/87/Hyperbolic_Paraboloid_Quadric.png',
    alt: 'Hyperbolic paraboloid',
    cap: 'A hyperbolic paraboloid — drawn by a moving straight line.',
    diagram: true,
  },
  {
    title: 'Three-compass scaling',
    body: 'For Bill\'s <em>Kontinuität</em>, the small plaster model was scaled <strong>4×</strong> onto the 180-ton granite using a three-compass jig — the medieval <em>Drei-Zirkelmethode</em>. A medieval method, applied to the largest granite monolith carved since antiquity.',
    img: 'https://upload.wikimedia.org/wikipedia/commons/7/79/M%C3%B6bius_Strip.jpg',
    alt: 'Möbius strip',
    cap: 'The topology being scaled — a Möbius strip.',
    diagram: true,
  },
  {
    title: 'Hand polish',
    body: 'Finish is by hand, even on monoliths. The high-gloss surface is what keeps the contrast between <em>soft swept form</em> and <em>granular granite</em> readable — what Fischer\'s 1999 essay calls the only real source of the sculpture\'s fascination.',
    img: 'https://upload.wikimedia.org/wikipedia/commons/1/17/Max_Bill_Kontinuitaet.jpg',
    alt: 'Polished detail of Kontinuität',
    cap: 'The finish — light hits granular granite as a single soft curve.',
  },
];
</script>

<style scoped>
.smethod { margin: 1.4rem 0; border-top: 1px solid var(--vein); }
.smethod__row {
  display: grid;
  grid-template-columns: 60px minmax(0, 1.4fr) minmax(0, 1fr);
  gap: 1.6rem;
  padding: 1.4rem 0;
  border-bottom: 1px solid var(--vein);
  align-items: start;
}
.smethod__num {
  font-family: var(--font-body);
  font-size: 2rem;
  font-weight: 700;
  color: var(--accent);
  line-height: 0.95;
}
.smethod__h {
  font-family: var(--font-body);
  font-size: 1.3rem;
  font-weight: 600;
  color: var(--slate);
  margin: 0 0 0.3rem;
}
.smethod__p {
  font-size: 1rem;
  line-height: 1.6;
  color: var(--slate-soft);
  margin: 0;
}
.smethod__p :deep(strong) { color: var(--slate); font-weight: 600; }
.smethod__p :deep(em) { font-style: italic; }
.smethod__fig { margin: 0; }
.smethod__fig img {
  width: 100%;
  height: auto;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 3px;
}
.smethod__fig--diagram img { object-fit: contain; background: var(--paper-deep); padding: 0.8rem; }
.smethod__fig figcaption {
  font-size: 0.82rem;
  color: var(--slate-soft);
  font-style: italic;
  margin-top: 0.3rem;
  line-height: 1.4;
}
@media (max-width: 1100px) {
  .smethod__row { grid-template-columns: 50px 1fr; }
  .smethod__fig { grid-column: 1 / -1; max-width: 380px; }
}
@media (max-width: 700px) {
  .smethod__row { grid-template-columns: 1fr; gap: 0.4rem; }
}
</style>
